import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, Video, Loader2 } from 'lucide-react';
import { API } from '../config/api';



const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB for stories

export default function CreateStoryPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const fileInputRef = useRef(null);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Only images and videos are supported');
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large (max 30MB for stories)');
      return;
    }

    // For video, check duration (max 60 seconds) - we'll validate on backend
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setMediaType(isImage ? 'image' : 'video');
  };

  const handleSubmit = async () => {
    if (!mediaFile) {
      toast.error('Please select an image or video');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('media', mediaFile);
      formData.append('media_type', mediaType);

      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const res = await fetch(`${API}/stories/create`, {
        method: 'POST',
        headers,
        body: formData
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create story');
      }

      console.log('Story created successfully:', data);
      toast.success('Story posted!');
      navigate('/feed');
    } catch (err) {
      console.error('Story creation error:', err);
      toast.error(err.message || 'Failed to create story');
    } finally {
      setLoading(false);
    }
  };

  const clearMedia = () => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="create-story-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold">Create Story</h1>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <Card className="glass-card border-border overflow-hidden">
          {mediaPreview ? (
            <div className="relative aspect-[9/16] bg-black">
              {mediaType === 'video' ? (
                <video
                  src={mediaPreview}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                />
              ) : (
                <img
                  src={mediaPreview}
                  alt=""
                  className="w-full h-full object-contain"
                />
              )}
              
              <button
                onClick={clearMedia}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/50 flex items-center justify-center hover:bg-background/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div
              className="aspect-[9/16] border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-secondary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/mov,video/webm"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">
                Add photo or video
              </p>
              <p className="text-xs text-muted-foreground">
                Max 30MB • Videos up to 60 seconds
              </p>
            </div>
          )}

          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Your story will be visible for 24 hours
            </p>
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit} 
                disabled={loading || !mediaFile}
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Share Story
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
